import { useMemo, useState } from 'react';
import { Card, ConfigProvider, Input, Space, Table, Tag, Typography } from 'antd';
import { FireOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

import { formatNumber } from '@/lib/format';
import type { TenantSpend } from './hooks';

const { Text } = Typography;

// Палитра прототипа (design/platform/standalone.html): бирюза-акцент, трек бара, тёплый hot-тег.
const ACCENT = '#2DBDB8';
const BAR_TRACK = '#EEF2F6';
const HEADER_BG = '#F5F8FB';
const BORDER = '#E3EAF0';

// Доля расхода, при которой оптика подсвечивается как «жжёт непропорционально».
const HOT_SHARE = 0.3;

interface SpendRow extends TenantSpend {
  share: number;
  barPct: number;
  isHot: boolean;
}

interface SpendTableProps {
  spend: TenantSpend[];
  loading?: boolean;
}

// Бар-пилюля расхода (как в прототипе): скруглённый трек + заливка. Заменяет
// AntD <Progress>, у которого угловатый вид не совпадал с дизайном.
function SpendBar({ pct }: { pct: number }): JSX.Element {
  return (
    <div
      style={{
        height: 5,
        borderRadius: 999,
        background: BAR_TRACK,
        width: '100%',
        maxWidth: 230,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: '100%', borderRadius: 999, background: ACCENT, width: `${pct}%` }} />
    </div>
  );
}

// Таблица «Расход по оптикам» (M5.4). Поиск по названию оптики + сортировка по
// месячному расходу. Колонку «Город» из прототипа не показываем — в модели
// тенанта города нет.
export function SpendTable({ spend, loading }: SpendTableProps): JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [sortDesc, setSortDesc] = useState(true);

  const totalMonth = useMemo(() => spend.reduce((a, s) => a + s.month, 0), [spend]);
  const totalToday = useMemo(() => spend.reduce((a, s) => a + s.today, 0), [spend]);
  const maxMonth = useMemo(() => Math.max(1, ...spend.map((s) => s.month)), [spend]);

  const rows = useMemo<SpendRow[]>(() => {
    const q = search.trim().toLowerCase();
    return spend
      .filter((s) => !q || s.name.toLowerCase().includes(q))
      .slice()
      .sort((a, b) => (sortDesc ? b.month - a.month : a.month - b.month))
      .map((s) => ({
        ...s,
        share: totalMonth > 0 ? s.month / totalMonth : 0,
        barPct: Math.max(4, Math.round((s.month / maxMonth) * 100)),
        isHot: totalMonth > 0 && s.month / totalMonth > HOT_SHARE,
      }));
  }, [spend, search, sortDesc, totalMonth, maxMonth]);

  const columns: ColumnsType<SpendRow> = [
    {
      title: t('platform.spend.optic'),
      key: 'name',
      // Фикс-ширина: иначе колонка схлопывается под короткие имена, а «Итого по
      // платформе» в summary ломается по буквам (см. дизайн-ревизию).
      width: 240,
      render: (_v, r) => (
        <Space size={8}>
          <Text strong style={{ fontSize: 13 }}>
            {r.name}
          </Text>
          {r.isHot && (
            <Tag
              icon={<FireOutlined />}
              style={{
                margin: 0,
                border: 'none',
                borderRadius: 999,
                background: '#FCEEDB',
                color: '#A36912',
                fontWeight: 600,
              }}
            >
              {Math.round(r.share * 100)}%
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('platform.spend.month'),
      key: 'month',
      width: 260,
      sorter: (a, b) => a.month - b.month,
      sortOrder: sortDesc ? 'descend' : 'ascend',
      render: (_v, r) => (
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Text className="sl-mono" style={{ fontSize: 13 }}>
            {formatNumber(r.month)}
          </Text>
          <SpendBar pct={r.barPct} />
        </Space>
      ),
    },
    {
      title: t('platform.spend.today'),
      dataIndex: 'today',
      key: 'today',
      width: 110,
      align: 'right',
      render: (v: number) => (
        <Text className="sl-mono" type="secondary" style={{ fontSize: 13 }}>
          {formatNumber(v)}
        </Text>
      ),
    },
    {
      title: t('platform.spend.share'),
      key: 'share',
      width: 90,
      align: 'right',
      render: (_v, r) => (
        <Text strong style={{ fontSize: 13 }}>
          {Math.round(r.share * 100)}%
        </Text>
      ),
    },
  ];

  return (
    <Card
      variant="outlined"
      styles={{ body: { padding: 0 } }}
      title={t('platform.spend.title')}
      extra={
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.35)' }} />}
          placeholder={t('platform.spend.search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 240 }}
          data-testid="spend-search"
        />
      }
    >
      {/* Токены Table локально: убираем угловатую серую подсветку сортируемой
          колонки (bodySortBg=transparent) и подгоняем шапку под палитру прототипа. */}
      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerBg: HEADER_BG,
              headerSortActiveBg: HEADER_BG,
              headerSortHoverBg: '#EFF3F7',
              bodySortBg: 'transparent',
              borderColor: BORDER,
            },
          },
        }}
      >
        <Table<SpendRow>
          rowKey="tenant_id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          size="middle"
          onChange={(_p, _f, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            if (s && s.order) setSortDesc(s.order === 'descend');
          }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <Text strong style={{ whiteSpace: 'nowrap' }}>
                  {t('platform.spend.total')}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <Text strong className="sl-mono">
                  {formatNumber(totalMonth)} SMS
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <Text strong className="sl-mono">
                  {formatNumber(totalToday)}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                <Text strong>100%</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
          locale={{ emptyText: t('platform.spend.empty') }}
        />
      </ConfigProvider>
    </Card>
  );
}
